import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ClientRoleEnum } from 'src/app/enums/client-role.enum';
import { PasswordOptionEnum } from 'src/app/enums/password-option.enum';
import { RoleSlugEnum } from 'src/app/enums/role-slug.enum';
import { AddUserDialogData } from 'src/app/interfaces/add-user-dialog-data.interface';
import { Company } from 'src/app/models/company.model';
import { Role } from 'src/app/models/role.model';
import { User } from 'src/app/models/user.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { RegexService } from 'src/app/services/regex.service';
import { pairwise, startWith } from 'rxjs';

@Component({
  selector: 'app-add-user-dialog',
  templateUrl: './add-user-dialog.component.html',
  styleUrls: ['./add-user-dialog.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatIconModule,
    MatButtonModule,
    MatAutocompleteModule,
  ],
})
export class AddUserDialogComponent implements OnInit {
  protected readonly PasswordOptionEnum = PasswordOptionEnum;
  protected readonly RoleSlugEnum = RoleSlugEnum;
  protected readonly ClientRoleEnum = ClientRoleEnum;

  private readonly dialog_ref = inject(MatDialogRef<AddUserDialogComponent, User>);
  private readonly fb = inject(FormBuilder);
  private readonly regex_service = inject(RegexService);
  private readonly api = inject(ApiService);
  private readonly permissions = inject(PermissionsService);
  private readonly destroy_ref = inject(DestroyRef);

  readonly data = inject<AddUserDialogData>(MAT_DIALOG_DATA, { optional: true }) ?? {};
  readonly company_id = this.data.company_id ?? null;
  readonly roles = signal<Role[]>([]);
  readonly available_customers = signal<Company[]>([]);
  readonly customer_selected = signal<Company | null>(null);
  readonly is_client_creation_locked = computed(() => this.data.lock_client_fields === true || this.company_id != null);
  readonly company_user_roles = computed(() =>
    this.roles().filter(role => role.slug === RoleSlugEnum.CLIENT || role.slug === RoleSlugEnum.PARTNER)
  );

  readonly client_select_control = new FormControl('');
  readonly locked_company_control = new FormControl({
    value: this.data.company_name ?? (this.company_id ? `Empresa #${this.company_id}` : ''),
    disabled: true,
  });
  readonly selected_role = signal<RoleSlugEnum | ''>(this.data.role ?? '');
  readonly selected_password_option = signal<PasswordOptionEnum>(PasswordOptionEnum.WITHOUT_PASSWORD_NO_NOTIFY);
  readonly passwords_match = signal(true);
  readonly display_company_for_autocomplete = (company: Company | string | null): string => {
    if (company === null || typeof company === 'string') {
      return company ?? '';
    }

    return company.fact_name;
  };

  readonly user_form = this.buildUserForm();

  ngOnInit(): void {
    this.initRoles();
    this.listenControlSearch();
    this.listenForm();
    this.user_form
      .get('role')
      ?.valueChanges.pipe(startWith(this.user_form.get('role')?.value), pairwise(), takeUntilDestroyed(this.destroy_ref))
      .subscribe(([previous_role, role]) => {
        this.selected_role.set((role as RoleSlugEnum | '') ?? '');
        if (previous_role !== role) {
          this.clearRoleDependentFields(role as RoleSlugEnum | null);
        }
        this.syncValidators();
      });
    this.user_form
      .get('password_option')
      ?.valueChanges.pipe(startWith(this.user_form.get('password_option')?.value), pairwise(), takeUntilDestroyed(this.destroy_ref))
      .subscribe(([previous_option, password_option]) => {
        this.selected_password_option.set((password_option as PasswordOptionEnum) ?? PasswordOptionEnum.WITHOUT_PASSWORD_NO_NOTIFY);
        if (previous_option !== password_option) {
          this.clearPasswordDependentFields();
        }
        this.syncValidators();
      });
    this.applyPresetFields();
    this.selected_role.set((this.user_form.getRawValue()['role'] as RoleSlugEnum | '') ?? '');
    this.syncValidators();
  }

  selectCustomer(company: Company): void {
    this.customer_selected.set(company);
    this.user_form.patchValue({ company_id: company.id });
  }

  resetCustomer(): void {
    this.available_customers.set([]);
    this.customer_selected.set(null);
    this.user_form.patchValue({ company_id: null });
  }

  submit(): void {
    if (!this.validateBeforeSubmit()) {
      return;
    }

    const raw_value = this.user_form.getRawValue();
    const body: Record<string, unknown> = { ...raw_value };
    delete body['password_valid'];

    if (body['password'] === '') {
      delete body['password'];
      delete body['password_confirmation'];
    }

    this.api
      .createUser(body as never)
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(user => {
        this.dialog_ref.close(user);
      });
  }

  private buildUserForm() {
    const phone_pattern = this.regex_service.getRegex()['phone'];

    return this.fb.group({
      name: [this.data.name || '', Validators.required],
      surnames: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      sex: ['M', Validators.required],
      birthday: [''],
      phone: ['', phone_pattern ? Validators.pattern(phone_pattern) : []],
      role: ['', Validators.required],
      company_id: [null as number | null],
      password: [''],
      password_confirmation: [''],
      password_valid: [false],
      client_role: [''],
      password_option: [PasswordOptionEnum.WITHOUT_PASSWORD_NO_NOTIFY, Validators.required],
    });
  }

  private validateBeforeSubmit(): boolean {
    this.syncValidators();
    this.checkPasswords();
    this.user_form.markAllAsTouched();

    return this.user_form.valid;
  }

  private syncValidators(): void {
    const role = this.user_form.get('role')?.value as RoleSlugEnum | null;
    if (role) {
      this.updateValidatorsByRole(role);
      return;
    }

    this.updatePasswordValidators();
  }

  private applyPresetFields(): void {
    const preset_role = this.data.role ?? (this.company_id ? RoleSlugEnum.CLIENT : null);
    const preset_client_role = this.data.client_role ?? (preset_role === RoleSlugEnum.CLIENT ? ClientRoleEnum.BUSINESS : null);
    const preset_company_id = this.company_id ?? this.data.company_id ?? null;

    if (preset_role) {
      this.user_form.patchValue({
        role: preset_role,
        client_role: preset_client_role,
        company_id: preset_company_id,
      });
      this.updateValidatorsByRole(preset_role);
    }

    if (this.is_client_creation_locked()) {
      // Company is fixed; role stays editable unless an explicit preset role was provided.
      this.user_form.get('company_id')?.disable({ emitEvent: false });
      this.client_select_control.disable({ emitEvent: false });
      this.locked_company_control.disable({ emitEvent: false });
      if (this.data.role) {
        this.user_form.get('role')?.disable({ emitEvent: false });
        this.user_form.get('client_role')?.disable({ emitEvent: false });
      }
    }

    if (preset_company_id && !this.data.company_name) {
      this.api
        .getCompany(preset_company_id.toString())
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe(company => {
          this.locked_company_control.setValue(company.fact_name, { emitEvent: false });
        });
    }
  }

  private listenForm(): void {
    this.user_form.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(form_value => {
      const passwords_match = form_value['password'] === form_value['password_confirmation'];
      this.passwords_match.set(passwords_match);
      this.user_form.patchValue({ password_valid: passwords_match }, { emitEvent: false });
    });

    this.user_form
      .get('password')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(() => {
        this.checkPasswords();
      });

    this.user_form
      .get('password_confirmation')
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe(() => {
        this.checkPasswords();
      });
  }

  private checkPasswords(): void {
    const password = this.user_form.get('password')?.value || '';
    const confirmation = this.user_form.get('password_confirmation')?.value || '';
    const passwords_match = password === confirmation;

    this.passwords_match.set(passwords_match);
    this.user_form.patchValue({ password_valid: passwords_match }, { emitEvent: false });
  }

  private listenControlSearch(): void {
    this.client_select_control.valueChanges.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(search_string => {
      if (search_string != null && search_string.length >= 3) {
        this.api
          .getCompanies({ search: search_string })
          .pipe(takeUntilDestroyed(this.destroy_ref))
          .subscribe(response => {
            this.available_customers.set(response.data);
          });
        return;
      }

      this.available_customers.set([]);
    });
  }

  private initRoles(): void {
    this.permissions.rolesObservable.pipe(takeUntilDestroyed(this.destroy_ref)).subscribe(roles_by_slug => {
      if (roles_by_slug != null) {
        this.roles.set(Object.keys(roles_by_slug).map(slug => roles_by_slug[slug]));
      }
    });
  }

  private clearRoleDependentFields(role: RoleSlugEnum | null): void {
    if (!this.is_client_creation_locked() && role !== RoleSlugEnum.CLIENT && role !== RoleSlugEnum.PARTNER) {
      this.user_form.patchValue({ company_id: null, client_role: '' }, { emitEvent: false });
      this.client_select_control.reset('', { emitEvent: false });
      this.available_customers.set([]);
      this.customer_selected.set(null);
    }

    if (role !== RoleSlugEnum.CLIENT) {
      this.user_form.patchValue({ client_role: '' }, { emitEvent: false });
    }

    if (role === RoleSlugEnum.CLIENT && !this.user_form.get('client_role')?.value) {
      this.user_form.patchValue({ client_role: ClientRoleEnum.BUSINESS }, { emitEvent: false });
    }

    if (role !== RoleSlugEnum.WORKER) {
      this.user_form.patchValue({ birthday: '', phone: '' }, { emitEvent: false });
    }
  }

  private clearPasswordDependentFields(): void {
    this.user_form.patchValue(
      {
        password: '',
        password_confirmation: '',
        password_valid: false,
      },
      { emitEvent: false }
    );
  }

  private updateValidatorsByRole(role: RoleSlugEnum): void {
    const required = Validators.required;
    const email = Validators.email;
    const phone_pattern = this.regex_service.getRegex()['phone'];
    const phone_validator = phone_pattern ? Validators.pattern(phone_pattern) : null;

    const name = this.user_form.get('name');
    const surnames = this.user_form.get('surnames');
    const email_control = this.user_form.get('email');
    const sex = this.user_form.get('sex');
    const birthday = this.user_form.get('birthday');
    const phone = this.user_form.get('phone');
    const company_id_control = this.user_form.get('company_id');
    const client_role = this.user_form.get('client_role');

    if (role === RoleSlugEnum.WORKER) {
      name?.setValidators([required]);
      surnames?.setValidators([required]);
      email_control?.setValidators([required, email]);
      sex?.setValidators([required]);
      birthday?.setValidators([required]);
      phone?.setValidators([required, ...(phone_validator ? [phone_validator] : [])]);
      company_id_control?.clearValidators();
      client_role?.clearValidators();
    } else if (role === RoleSlugEnum.CLIENT || role === RoleSlugEnum.PARTNER) {
      name?.setValidators([required]);
      surnames?.setValidators([required]);
      email_control?.setValidators([required, email]);
      sex?.setValidators([required]);
      birthday?.clearValidators();
      phone?.clearValidators();
      company_id_control?.setValidators([required]);

      if (role === RoleSlugEnum.CLIENT) {
        client_role?.setValidators([required]);
      } else {
        client_role?.clearValidators();
      }
    }

    company_id_control?.updateValueAndValidity();
    client_role?.updateValueAndValidity();
    name?.updateValueAndValidity();
    surnames?.updateValueAndValidity();
    email_control?.updateValueAndValidity();
    sex?.updateValueAndValidity();
    birthday?.updateValueAndValidity();
    phone?.updateValueAndValidity();
    this.updatePasswordValidators();
  }

  private updatePasswordValidators(): void {
    const required = Validators.required;
    const min_length_8 = Validators.minLength(8);
    const password = this.user_form.get('password');
    const password_confirmation = this.user_form.get('password_confirmation');
    const password_valid = this.user_form.get('password_valid');
    const password_option = this.user_form.get('password_option')?.value;

    if (password_option === PasswordOptionEnum.WITH_PASSWORD) {
      password?.setValidators([required, min_length_8]);
      password_confirmation?.setValidators([required]);
      password_valid?.setValidators([Validators.requiredTrue]);
    } else {
      password?.clearValidators();
      password_confirmation?.clearValidators();
      password_valid?.clearValidators();
    }

    password?.updateValueAndValidity();
    password_confirmation?.updateValueAndValidity();
    password_valid?.updateValueAndValidity();
  }
}
